import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import {
  CREATE_DOCUMENT_UPLOAD_MUTATION, DOCUMENT_ACCESS_URL_QUERY, DOCUMENT_QUERY,
  DOCUMENTS_QUERY, RETRY_DOCUMENT_PROCESSING_MUTATION,
} from '../graphql/queries';
import { useUserGroups } from '../hooks/useUserGroups';
import { gql } from '../lib/api';

interface StoredDocument {
  id: string;
  fileName: string;
  scope: string;
  sizeBytes: number;
  uploadedAt: string;
  completedAt?: string | null;
  pageCount?: number | null;
  status: string;
  thumbnailStatus: string;
  error?: string | null;
  thumbnailError?: string | null;
  sha256?: string | null;
  duplicateOf?: string | null;
}

interface UploadJob {
  key: string;
  file: File;
  scope: string;
  state: 'queued' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  documentId?: string;
  error?: string;
}

interface DocumentConnection { items: StoredDocument[]; nextCursor?: string | null }
interface DocumentUpload {
  document: StoredDocument;
  url: string;
  fieldsJson: string;
  expiresAt: string;
  maxBytes: number;
  maxPages: number;
}

const scopes = [
  { value: 'COMMON', label: '共通・未設定' },
  { value: 'loop-hamburg', label: 'ループ ハンバーグ' },
  { value: 'koen', label: '好EN' },
];
const statuses: Record<string, string> = {
  UPLOADING: 'アップロード待ち', PROCESSING: '処理中', READY: '保存完了',
  REJECTED: '受付不可', DUPLICATE: '重複', EXPIRED: '期限切れ',
  FAILED: '処理失敗',
};
const maxBytes = 20 * 1024 * 1024;
const scopeLabel = (scope: string) => scopes.find((item) => item.value === scope)?.label ?? scope;
const formatSize = (size: number) => size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KiB` : `${(size / 1024 / 1024).toFixed(2)} MiB`;
const formatDate = (date: string) => new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit',
}).format(new Date(date));
const inProgress = (doc: StoredDocument) => doc.status === 'UPLOADING' || doc.status === 'PROCESSING' || (doc.status === 'READY' && doc.thumbnailStatus === 'PENDING');
const errorText = (error: unknown) => error instanceof Error ? error.message : '処理に失敗しました。';

const accessUrl = async (documentId: string, kind: 'ORIGINAL' | 'DOWNLOAD' | 'THUMBNAIL') => {
  const result = await gql<{ getDocumentAccessUrl: { url: string; expiresAt: string } }>(DOCUMENT_ACCESS_URL_QUERY, { documentId, kind });
  return result.getDocumentAccessUrl;
};

const StatusBadge = ({ doc }: { doc: StoredDocument }) => (
  <span className={`document-status document-status-${doc.status.toLowerCase()}`}>{statuses[doc.status] ?? doc.status}</span>
);

const DocumentThumbnail = ({ doc }: { doc: StoredDocument }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const thumbnail = useQuery({
    queryKey: ['document-thumbnail', doc.id, doc.completedAt, doc.thumbnailStatus],
    queryFn: () => accessUrl(doc.id, 'THUMBNAIL'),
    enabled: doc.status === 'READY' && doc.thumbnailStatus === 'READY',
    staleTime: 60_000, gcTime: 0, retry: false,
  });
  if (doc.status !== 'READY' || doc.thumbnailStatus !== 'READY') {
    return <span className="document-thumbnail-placeholder">PDF</span>;
  }
  if (thumbnail.isError || imageFailed) {
    return <button className="document-thumb-retry" type="button" disabled={thumbnail.isFetching} onClick={() => {
      void thumbnail.refetch().then((result) => { if (result.isSuccess) setImageFailed(false); });
    }}>画像を再読込</button>;
  }
  return thumbnail.data
    ? <img className="document-thumbnail" src={thumbnail.data.url} alt={`${doc.fileName}の1ページ目`} referrerPolicy="no-referrer" onError={() => setImageFailed(true)} />
    : <span className="document-thumbnail-placeholder">読込中</span>;
};

const UploadJobRow = ({ job, onRetry }: { job: UploadJob; onRetry: (key: string) => void }) => {
  const queryClient = useQueryClient();
  const document = useQuery({
    queryKey: ['document', job.documentId],
    queryFn: async () => {
      const { getDocument } = await gql<{ getDocument: StoredDocument | null }>(DOCUMENT_QUERY, { documentId: job.documentId });
      if (!getDocument) throw new Error('書類が見つかりません。');
      return getDocument;
    },
    enabled: job.state === 'uploaded' && !!job.documentId,
    retry: false,
    refetchInterval: (query) => query.state.error ? false : (!query.state.data || inProgress(query.state.data)) ? 4_000 : false,
  });
  const currentStatus = document.data?.status;
  const currentThumbnailStatus = document.data?.thumbnailStatus;
  useEffect(() => {
    if (currentStatus && currentStatus !== 'UPLOADING' && currentStatus !== 'PROCESSING') {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    }
  }, [queryClient, currentStatus, currentThumbnailStatus]);
  return <li className="document-upload-job">
    <div><strong>{job.file.name}</strong><small>{scopeLabel(job.scope)} · {formatSize(job.file.size)}</small></div>
    <div className="document-upload-state" aria-live="polite">
      {job.state === 'queued' && <span>順番待ち</span>}
      {job.state === 'uploading' && <><progress value={job.progress} max={100} aria-label={`${job.file.name}のアップロード進捗`} /><span>{job.progress}%</span></>}
      {job.state === 'uploaded' && (document.data ? <><StatusBadge doc={document.data} />{document.data.error && <small className="document-error">{document.data.error}</small>}</> : <span>{document.isError ? '状態を取得できません' : '送信完了・確認中'}</span>)}
      {document.isError && job.state === 'uploaded' && <button type="button" onClick={() => void document.refetch()}>状態を再確認</button>}
      {job.state === 'error' && <><span className="document-error">{job.error}</span><button type="button" onClick={() => onRetry(job.key)}>アップロード再試行</button></>}
    </div>
  </li>;
};

const DocumentPreview = ({ doc, onClose }: { doc: StoredDocument; onClose: () => void }) => {
  const dialog = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [renderError, setRenderError] = useState('');
  const preview = useQuery({ queryKey: ['document-preview', doc.id], queryFn: () => accessUrl(doc.id, 'ORIGINAL'), staleTime: 0, gcTime: 0, retry: false, refetchOnWindowFocus: false });
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);
  const sourceUrl = preview.data?.url;
  const sourceUpdatedAt = preview.dataUpdatedAt;
  const fetchingUrl = preview.isFetching;
  useEffect(() => {
    let active = true;
    let loadingTask: PDFDocumentLoadingTask | undefined;
    setPdf(null);
    setPageNumber(1);
    setPdfError('');
    setLoadingPdf(!!sourceUrl || fetchingUrl);
    if (sourceUrl && !fetchingUrl) {
      void (async () => {
        try {
          const pdfjs = await import('pdfjs-dist');
          if (!active) return;
          pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
          loadingTask = pdfjs.getDocument({ url: sourceUrl, withCredentials: false, disableRange: true });
          const loaded = await loadingTask.promise;
          if (active) setPdf(loaded);
        } catch (error) {
          if (active) setPdfError(`PDFを読み込めませんでした。${errorText(error)}`);
        } finally {
          if (active) setLoadingPdf(false);
        }
      })();
    }
    return () => {
      active = false;
      if (loadingTask) void loadingTask.destroy().catch(() => undefined);
    };
  }, [sourceUrl, sourceUpdatedAt, fetchingUrl]);

  useEffect(() => {
    if (!pdf || !canvas.current) return;
    let active = true;
    let renderTask: RenderTask | undefined;
    const element = canvas.current;
    setRendering(true);
    setRenderError('');
    void (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (!active) return;
        const originalViewport = page.getViewport({ scale: 1 });
        if (!Number.isFinite(originalViewport.width) || !Number.isFinite(originalViewport.height)
          || originalViewport.width <= 0 || originalViewport.height <= 0) throw new Error('ページの寸法が不正です。');
        const width = Math.min(800, Math.max(240, element.parentElement!.clientWidth - 32));
        const viewport = page.getViewport({ scale: Math.min(width / originalViewport.width, 2048 / originalViewport.height) });
        const outputScale = Math.min(window.devicePixelRatio || 1, 2);
        element.width = Math.floor(viewport.width * outputScale);
        element.height = Math.floor(viewport.height * outputScale);
        element.style.width = `${Math.floor(viewport.width)}px`;
        element.style.height = `${Math.floor(viewport.height)}px`;
        renderTask = page.render({
          canvas: element, viewport,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
        });
        await renderTask.promise;
      } catch (error) {
        if (active) setRenderError(`ページを表示できませんでした。${errorText(error)}`);
      } finally {
        if (active) setRendering(false);
      }
    })();
    return () => { active = false; renderTask?.cancel(); };
  }, [pdf, pageNumber]);

  const busy = preview.isPending || preview.isFetching || loadingPdf || rendering;
  return <dialog ref={dialog} className="document-preview-dialog" aria-labelledby="document-preview-title" onCancel={onClose}>
    <div className="document-preview-heading"><h2 id="document-preview-title">{doc.fileName}</h2><button type="button" autoFocus onClick={onClose}>閉じる</button></div>
    <p>{scopeLabel(doc.scope)} · {pdf?.numPages ?? doc.pageCount ?? '—'}ページ · {formatSize(doc.sizeBytes)}</p>
    <div className="document-preview-toolbar" aria-label="PDFのページ操作">
      <button type="button" disabled={!pdf || busy || pageNumber <= 1} onClick={() => setPageNumber((current) => current - 1)}>前のページ</button>
      <span aria-live="polite">{pdf ? `${pageNumber} / ${pdf.numPages} ページ` : '— / — ページ'}</span>
      <button type="button" disabled={!pdf || busy || pageNumber >= pdf.numPages} onClick={() => setPageNumber((current) => current + 1)}>次のページ</button>
      <button type="button" disabled={preview.isFetching} onClick={() => void preview.refetch()}>PDFを再読込</button>
    </div>
    {busy && <p role="status">{rendering ? 'ページを表示しています…' : 'PDFを読み込んでいます…'}</p>}
    {(preview.isError || pdfError || renderError) && <p className="document-error" role="alert">{preview.isError ? errorText(preview.error) : pdfError || renderError} 「PDFを再読込」で署名付きURLを再取得できます。</p>}
    <p className="document-hint">前後のページボタンで全ページを閲覧できます。原本の保存は一覧の「原本ダウンロード」をご利用ください。</p>
    <div className="document-preview-canvas-scroll" aria-busy={busy}>
      <canvas ref={canvas} hidden={!pdf || !!renderError} role="img" aria-label={`${doc.fileName} ${pageNumber}ページ目`} />
    </div>
  </dialog>;
};

export const DocumentsPage = () => {
  const isAdmin = useUserGroups().includes('admin');
  const queryClient = useQueryClient();
  const [uploadScope, setUploadScope] = useState('COMMON');
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(false);
  const [scope, setScope] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<StoredDocument | null>(null);
  const [actionError, setActionError] = useState('');
  const [accessBusy, setAccessBusy] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const queue = useRef<UploadJob[]>([]);
  const running = useRef(false);
  const mounted = useRef(true);
  const requests = useRef(new Set<XMLHttpRequest>());
  const invalidDates = !!fromDate && !!toDate && fromDate > toDate;

  useEffect(() => {
    mounted.current = true;
    const activeRequests = requests.current;
    return () => {
      mounted.current = false;
      queue.current = [];
      for (const request of activeRequests) request.abort();
    };
  }, []);

  const documents = useInfiniteQuery({
    queryKey: ['documents', scope, status, fromDate, toDate],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => (await gql<{ listDocuments: DocumentConnection }>(DOCUMENTS_QUERY, {
      scope: scope || null, status: status || null, fromDate: fromDate || null,
      toDate: toDate || null, cursor: pageParam ?? null, limit: 30,
    })).listDocuments,
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: isAdmin && !invalidDates,
    retry: false,
    refetchInterval: (query) => query.state.error ? false : query.state.data?.pages.some((page) => page.items.some(inProgress)) || jobs.some((job) => job.state === 'queued' || job.state === 'uploading') ? 4_000 : false,
  });
  const items = Array.from(new Map((documents.data?.pages.flatMap((page) => page.items) ?? []).map((doc) => [doc.id, doc])).values());
  const visibleSelection = items.filter((doc) => selected.has(doc.id));
  const allSelected = items.length > 0 && visibleSelection.length === items.length;
  const retryProcessing = useMutation({
    mutationFn: async (documentId: string) => (await gql<{ retryDocumentProcessing: StoredDocument }>(RETRY_DOCUMENT_PROCESSING_MUTATION, { documentId })).retryDocumentProcessing,
    onSuccess: (doc) => {
      queryClient.setQueryData(['document', doc.id], doc);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['document-thumbnail', doc.id] });
    },
    onError: (error) => setActionError(errorText(error)),
  });

  const updateJob = (key: string, patch: Partial<UploadJob>) => {
    if (mounted.current) setJobs((current) => current.map((job) => job.key === key ? { ...job, ...patch } : job));
  };

  const uploadOne = async (job: UploadJob) => {
    updateJob(job.key, { state: 'uploading', progress: 0, error: undefined });
    try {
      const { createDocumentUpload: upload } = await gql<{ createDocumentUpload: DocumentUpload }>(CREATE_DOCUMENT_UPLOAD_MUTATION, {
        input: { fileName: job.file.name, sizeBytes: job.file.size, scope: job.scope, contentType: 'application/pdf' },
      });
      if (!mounted.current) return;
      updateJob(job.key, { documentId: upload.document.id });
      if (job.file.size > upload.maxBytes) throw new Error('サーバーのファイルサイズ制限を超えています。');
      const fields: unknown = JSON.parse(upload.fieldsJson);
      if (!fields || typeof fields !== 'object' || Array.isArray(fields)) throw new Error('アップロード情報が不正です。');
      const form = new FormData();
      for (const [name, value] of Object.entries(fields)) {
        if (typeof value !== 'string') throw new Error('アップロード情報が不正です。');
        form.append(name, value);
      }
      // S3の署名付きPOSTでは署名対象フィールドを先に、ファイルを最後に送る。
      form.append('file', job.file);
      await new Promise<void>((resolve, reject) => {
        const request = new XMLHttpRequest();
        requests.current.add(request);
        request.open('POST', upload.url);
        request.timeout = 10 * 60_000;
        request.upload.onprogress = (event) => {
          if (event.lengthComputable) updateJob(job.key, { progress: Math.min(99, Math.round(event.loaded / event.total * 100)) });
        };
        request.onload = () => request.status >= 200 && request.status < 300 ? resolve() : reject(new Error(`アップロードに失敗しました（HTTP ${request.status}）。再試行してください。`));
        request.onerror = () => reject(new Error('通信に失敗しました。接続を確認して再試行してください。'));
        request.ontimeout = () => reject(new Error('アップロードがタイムアウトしました。再試行してください。'));
        request.onabort = () => reject(new Error('アップロードを中断しました。'));
        request.onloadend = () => requests.current.delete(request);
        request.send(form);
      });
      updateJob(job.key, { state: 'uploaded', progress: 100 });
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch (error) {
      updateJob(job.key, { state: 'error', error: errorText(error) });
    }
  };

  const pumpQueue = async () => {
    if (running.current) return;
    running.current = true;
    const worker = async () => {
      while (mounted.current) {
        const job = queue.current.shift();
        if (!job) break;
        await uploadOne(job);
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    running.current = false;
  };

  const addFiles = (files: File[]) => {
    if (!isAdmin || !files.length) return;
    if (files.length > 100) { setNotice('1回に選択できるPDFは100件までです。'); return; }
    const rejected: string[] = [];
    const next = files.flatMap((file): UploadJob[] => {
      if (!/\.pdf$/i.test(file.name) || (file.type && file.type !== 'application/pdf')) { rejected.push(`${file.name}: PDFのみ選択できます。`); return []; }
      if (file.size <= 0 || file.size > maxBytes) { rejected.push(`${file.name}: 0バイトを超え20 MiB以下のPDFを選択してください。`); return []; }
      return [{ key: crypto.randomUUID(), file, scope: uploadScope, state: 'queued', progress: 0 }];
    });
    setNotice(rejected.join('\n'));
    setJobs((current) => [...current, ...next]);
    queue.current.push(...next);
    void pumpQueue();
  };

  const retryUpload = (key: string) => {
    const job = jobs.find((item) => item.key === key && item.state === 'error');
    if (!job) return;
    updateJob(key, { state: 'queued', progress: 0, documentId: undefined, error: undefined });
    queue.current.push({ ...job, state: 'queued', documentId: undefined });
    void pumpQueue();
  };

  const download = async (doc: StoredDocument) => {
    setAccessBusy(doc.id);
    setActionError('');
    try {
      const { url } = await accessUrl(doc.id, 'DOWNLOAD');
      const link = window.document.createElement('a');
      link.href = url;
      link.rel = 'noopener noreferrer';
      link.download = doc.fileName;
      link.click();
    } catch (error) { setActionError(errorText(error)); }
    finally { setAccessBusy(null); }
  };

  const openDuplicate = async (id: string) => {
    setAccessBusy(id);
    setActionError('');
    try {
      const { getDocument: existing } = await gql<{ getDocument: StoredDocument | null }>(DOCUMENT_QUERY, { documentId: id });
      if (!existing || existing.status !== 'READY') throw new Error('既存の書類をプレビューできません。');
      setPreview(existing);
    } catch (error) { setActionError(errorText(error)); }
    finally { setAccessBusy(null); }
  };

  const changeFilter = (change: () => void) => { change(); setSelected(new Set()); };
  const toggleSelected = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (!isAdmin) return <p>証憑・資料は管理者専用です。</p>;
  return <div className="documents-page">
    <div className="documents-heading"><div><h1>証憑・資料</h1><p>PDF原本を安全に保存し、店舗別に整理します。</p></div><span className="document-admin-label">管理者専用</span></div>
    <section className="document-upload-panel" aria-labelledby="document-upload-title">
      <div className="document-section-heading"><h2 id="document-upload-title">PDFアップロード</h2><label>保存先 <select value={uploadScope} onChange={(event) => setUploadScope(event.target.value)}>{scopes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label></div>
      <div className={`document-drop-zone${dragging ? ' document-drop-zone-active' : ''}`} onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files)); }}>
        <strong>PDFをここにドラッグ＆ドロップ</strong><span>または</span><button type="button" className="document-primary-button" onClick={() => input.current?.click()}>PDFを選択</button>
        <input ref={input} type="file" accept=".pdf,application/pdf" multiple hidden onChange={(event) => { addFiles(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
        <small>1件20 MiB・200ページまで ／ 1回100件まで ／ 同時送信3件</small>
      </div>
      <p className="document-hint">ページ数・PDF形式はサーバーで検証します。暗号化・破損PDFは受付できません。保存先にかかわらず同一内容のPDFは重複として記録します。</p>
      {notice && <p className="document-error document-notice" role="alert">{notice}</p>}
      {jobs.length > 0 && <><div className="document-queue-heading"><span>今回のアップロード {jobs.length}件</span><button type="button" disabled={jobs.some((job) => job.state === 'queued' || job.state === 'uploading')} onClick={() => setJobs([])}>送信済み履歴を閉じる</button></div><ul className="document-upload-jobs">{jobs.map((job) => <UploadJobRow key={job.key} job={job} onRetry={retryUpload} />)}</ul></>}
    </section>
    <section className="document-list-panel" aria-labelledby="document-list-title">
      <div className="document-section-heading"><h2 id="document-list-title">保存書類</h2><button type="button" disabled={documents.isFetching || invalidDates} onClick={() => void documents.refetch()}>更新</button></div>
      <div className="document-filters">
        <label>保存先<select value={scope} onChange={(event) => changeFilter(() => setScope(event.target.value))}><option value="">すべて</option>{scopes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <label>状態<select value={status} onChange={(event) => changeFilter(() => setStatus(event.target.value))}><option value="">すべて</option>{Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>登録日（JST）から<input type="date" value={fromDate} onChange={(event) => changeFilter(() => setFromDate(event.target.value))} /></label>
        <label>登録日（JST）まで<input type="date" value={toDate} onChange={(event) => changeFilter(() => setToDate(event.target.value))} /></label>
      </div>
      {invalidDates && <p className="document-error" role="alert">開始日は終了日以前の日付を指定してください。</p>}
      <div className="document-selection"><span>表示済み {items.length}件 · 選択 {visibleSelection.length}件</span><small>新しい登録順。選択のみで外部への送信は行いません。</small></div>
      {actionError && <p className="document-error" role="alert">{actionError}</p>}
      {documents.isError && <p className="document-error" role="alert">一覧を取得できませんでした。{errorText(documents.error)} <button type="button" onClick={() => void documents.refetch()}>再読込</button></p>}
      {documents.isPending && !invalidDates && <p role="status">書類を読み込んでいます…</p>}
      {!documents.isPending && !documents.isError && !items.length && !invalidDates && <p className="document-empty">該当する書類はありません。</p>}
      {items.length > 0 && <div className="document-table-scroll"><table className="document-table"><thead><tr>
        <th><input type="checkbox" aria-label="表示済みの書類をすべて選択" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((doc) => doc.id)))} /></th>
        <th>プレビュー</th><th>書類名 / 保存先</th><th>登録日時（JST）</th><th>サイズ / ページ</th><th>状態</th><th>操作</th>
      </tr></thead><tbody>{items.map((doc) => <tr key={doc.id}>
        <td><input type="checkbox" aria-label={`${doc.fileName}を選択`} checked={selected.has(doc.id)} onChange={() => toggleSelected(doc.id)} /></td>
        <td><DocumentThumbnail doc={doc} /></td>
        <td className="document-name"><strong>{doc.fileName}</strong><small>{scopeLabel(doc.scope)}</small></td>
        <td className="document-date">{formatDate(doc.uploadedAt)}</td>
        <td>{formatSize(doc.sizeBytes)}<small>{doc.pageCount == null ? '—' : `${doc.pageCount}ページ`}</small></td>
        <td><StatusBadge doc={doc} />{doc.error && <small className="document-error">{doc.error}</small>}{doc.status === 'READY' && doc.thumbnailStatus !== 'READY' && <small>{doc.thumbnailStatus === 'FAILED' ? 'サムネイル生成失敗（原本は保存済み）' : 'サムネイル生成中'}</small>}{doc.thumbnailError && <small className="document-error">{doc.thumbnailError}</small>}</td>
        <td><div className="document-row-actions">
          {doc.status === 'READY' && <><button type="button" onClick={() => setPreview(doc)}>全ページ表示</button><button type="button" disabled={accessBusy !== null} onClick={() => void download(doc)}>{accessBusy === doc.id ? '取得中…' : '原本ダウンロード'}</button></>}
          {doc.status === 'READY' && doc.thumbnailStatus === 'FAILED' && <button type="button" disabled={retryProcessing.isPending} onClick={() => { setActionError(''); retryProcessing.mutate(doc.id); }}>サムネイル再生成</button>}
          {doc.status === 'FAILED' && <button type="button" disabled={retryProcessing.isPending} onClick={() => { setActionError(''); retryProcessing.mutate(doc.id); }}>処理再試行</button>}
          {doc.status === 'DUPLICATE' && doc.duplicateOf && <button type="button" disabled={accessBusy !== null} onClick={() => void openDuplicate(doc.duplicateOf!)}>既存書類を表示</button>}
          {(doc.status === 'REJECTED' || doc.status === 'EXPIRED') && <small>内容を確認してPDFを再アップロードしてください。</small>}
        </div></td>
      </tr>)}</tbody></table></div>}
      {documents.hasNextPage && <div className="document-more"><button type="button" disabled={documents.isFetching || invalidDates} onClick={() => void documents.fetchNextPage()}>{documents.isFetchingNextPage ? '読み込み中…' : '続きを読み込む'}</button></div>}
    </section>
    {preview && <DocumentPreview doc={preview} onClose={() => setPreview(null)} />}
  </div>;
};
